import { useState } from 'react'
import { Redirect, useParams } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { useAppSelector } from 'hooks/store'
import useLoadingDelay from 'hooks/useLoadingDelay'
import { loadingSpinnerPortal } from 'components/atoms/LoadingSpinner'
import ErrorMessage from 'components/atoms/ErrorMessage'
import NewAccountCreateBookmark from 'components/features/accountcreation/NewAccountCreateBookmark'
import NewAccountCreateYourPassword from 'components/features/accountcreation/NewAccountCreateYourPassword'
import NewAccountVerifyYourEmailAddress from 'components/features/accountcreation/NewAccountVerifyYourEmailAddress'
import NewAccountVerifyYourPhoneNumber from 'components/features/accountcreation/NewAccountVerifyYourPhoneNumber'
import {
  StartEmailVerificationResponse,
  START_EMAIL_VERIFICATION,
  StartPhoneVerificationResponse,
  START_PHONE_VERIFICATION
} from 'share/mutations/accountcreation'
import {
  AccountCreationError,
  CP_GENERAL_ERROR_MESSAGE,
  CP_ACCOUNT_DATA_ERROR_MESSAGE
} from 'share/utils/codeVerificationUtils'

import { MarginBlock } from './styles'

export interface NextData {
  userId?: string
  password?: string | null
  mobilePhone?: string | null
  emailAddress?: string | null
  contactType?: number | null
  verifyPhone: boolean
  verifyEmail: boolean
}

export interface NextStep {
  nextStep(data: NextData): void
}

interface ParamTypes {
  invitationId: string
}

function NewAccountMain(): JSX.Element {
  const authentication = useAppSelector((state) => state.authentication)
  const credentials = useAppSelector((state) => state.credentials)
  const [startEmailVerification, { loading: loadingEmailCode }] =
    useMutation<StartEmailVerificationResponse>(START_EMAIL_VERIFICATION)
  const [startPhoneVerification, { loading: loadingPhoneCode }] =
    useMutation<StartPhoneVerificationResponse>(START_PHONE_VERIFICATION)
  const [error, setError] = useState(AccountCreationError.NoError)
  const [enteredCode, setEnteredCode] = useState('')
  const [currentPage, setCurrentPage] = useState('createpassword')
  const [nextData, setNextData] = useState<NextData>({
    userId:
      authentication &&
      authentication.account &&
      authentication.account.userName
        ? authentication.account.userName
        : '',
    password: credentials && credentials.password ? credentials.password : null,
    mobilePhone: null,
    contactType: null,
    verifyPhone: true,
    verifyEmail: true
  })
  const { invitationId } = useParams<ParamTypes>()

  const loadings = loadingEmailCode || loadingPhoneCode

  const loadingDelay = useLoadingDelay(loadings)

  const nextStep = (data: NextData) => {
    console.log('data', data)
    console.log('currentPage', currentPage)
    if (currentPage === 'createpassword') {
      setNextData(data)
      console.dir(data)
      if (data.verifyPhone) {
        setCurrentPage('verifyphonenumber')
      } else {
        if (data.verifyEmail) {
          setCurrentPage('verifyemaildetails')
        } else {
          setCurrentPage('createbookmark')
        }
      }
    } else if (currentPage === 'verifyphonenumber') {
      setError(AccountCreationError.NoError)
      if (data.verifyEmail) {
        setCurrentPage('verifyemaildetails')
      } else {
        setCurrentPage('createbookmark')
      }
      setEnteredCode('')
    } else if (currentPage === 'verifyemaildetails') {
      setCurrentPage('createbookmark')
      setEnteredCode('')
    }
  }

  const resendEmailVerificationCode = async () => {
    try {
      await startEmailVerification()
    } catch (exc) {
      setError(AccountCreationError.GeneralError)
    }
    setError(AccountCreationError.NoError)
    setEnteredCode('')
  }

  const resendPhoneVerificationCode = async () => {
    try {
      await startPhoneVerification()
    } catch (exc) {
      setError(AccountCreationError.GeneralError)
    }
    setError(AccountCreationError.NoError)
    setEnteredCode('')
  }

  const renderFailViolator = () => {
    let errorMsg = ''
    switch (error) {
      case AccountCreationError.ExpiredCode:
        errorMsg = 'Your verification code has expired. '
        break
      case AccountCreationError.InvalidCode:
        errorMsg = 'Your verification code is incorrect. '
        break
      case AccountCreationError.GeneralError:
        errorMsg = CP_GENERAL_ERROR_MESSAGE
        break
      case AccountCreationError.AccountData:
        errorMsg = CP_ACCOUNT_DATA_ERROR_MESSAGE
        break
    }

    return (
      <ErrorMessage>
        <span>
          {errorMsg}
          <span
            hidden={
              error === AccountCreationError.GeneralError ||
              error === AccountCreationError.AccountData
            }
            role='button'
            onClick={
              currentPage === 'verifyphonenumber'
                ? resendPhoneVerificationCode
                : resendEmailVerificationCode
            }
          >
            Request new code.
          </span>
        </span>
      </ErrorMessage>
    )
  }

  if (currentPage === 'loginmain') {
    return (
      <Redirect
        to={{
          pathname: '/login'
        }}
      />
    )
  }

  return (
    <>
      {loadingDelay && loadingSpinnerPortal()}
      {error !== AccountCreationError.NoError && renderFailViolator()}
      {currentPage === 'createpassword' && (
        <>
          <NewAccountCreateYourPassword
            invitationId={invitationId}
            nextStep={nextStep}
            setError={setError}
          />
        </>
      )}
      {currentPage === 'verifyphonenumber' && (
        <NewAccountVerifyYourPhoneNumber
          codeValue={enteredCode}
          invitationId={invitationId}
          nextStep={nextStep}
          phoneNumber={nextData.mobilePhone ? nextData.mobilePhone : ''}
          resendVerificationCode={resendPhoneVerificationCode}
          setCode={setEnteredCode}
          setError={setError}
          verifyPhone={nextData.verifyPhone}
          verifyEmail={nextData.verifyEmail}
        />
      )}

      {currentPage === 'verifyemaildetails' && (
        <NewAccountVerifyYourEmailAddress
          codeValue={enteredCode}
          nextStep={nextStep}
          resendVerificationCode={resendEmailVerificationCode}
          setCode={setEnteredCode}
          setError={setError}
          verifyPhone={nextData.verifyPhone}
          verifyEmail={nextData.verifyEmail}
        />
      )}
      {currentPage === 'createbookmark' && (
        <>
          <MarginBlock />
          <NewAccountCreateBookmark />
        </>
      )}
    </>
  )
}

export default NewAccountMain
